/**
 * 按固定并发数映射数组。
 *
 * 设计说明：
 * `.puppy` 解析和导入可能同时处理大量 BRep payload。直接 `Promise.all`
 * 会把摘要计算、字符串解码或 Worker 注册一次性压入队列，导致大装配包在主线程和
 * Worker 队列上出现不必要的峰值。这里提供一个纯函数式的小型并发调度器，让协议层
 * 可以保持结果顺序，同时限制瞬时压力。
 *
 * @param items 输入数组。
 * @param concurrency 最大并发数。
 * @param mapper 单项映射函数。
 * @returns 与输入顺序一致的映射结果。
 */
export async function mapWithConcurrency<TItem, TResult>(
  items: readonly TItem[],
  concurrency: number,
  mapper: (item: TItem, index: number) => Promise<TResult>,
): Promise<TResult[]> {
  if (items.length === 0) {
    return []
  }

  const workerCount = Math.min(Math.max(1, Math.floor(concurrency)), items.length)
  const results = new Array<TResult>(items.length)
  let nextIndex = 0

  await Promise.all(Array.from({ length: workerCount }, async () => {
    while (nextIndex < items.length) {
      const currentIndex = nextIndex
      nextIndex += 1
      const item = items[currentIndex]!
      results[currentIndex] = await mapper(item, currentIndex)
    }
  }))

  return results
}
